import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, Fragment } from 'react';
import { Unknown } from '../Unknown';
import { Box } from './Box';
import { BirdComboBox } from '../BirdComboBox';
import { BIRDVisualization } from '../BIRDVisualization';
import { BirdCheckboxList } from '../BirdCheckboxList';
import { BirdTextField } from '../BirdTextField';
import { BirdImage } from '../BirdImage';
import { BirdSlider } from '../BirdSlider';
import { BirdText } from '../BirdText';
import { BirdRadioList } from '../BirdRadioList';
import { BirdTable } from '../BirdTable';
import { BirdCrosstab } from '../BirdCrosstab';
import { BirdButtonBar } from '../BirdButtonBar';
import { StackContainer } from '../StackContainer';
import { PromptContainer } from '../PromptContainer';
import { BirdWebContent } from '../BirdWebContent';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { PositionedLayout } from '../PositionedLayout';
import { Title } from '../Title';
import { FlexHeightLayout, hasFlexibleHeight } from '../FlexHeightLayout';
import { exposeTestId } from '../../config';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReportContextMenuState } from '../../store/reportContextMenu/ReportContextMenuState';
import { BirdJobContent } from '../BirdJobContent';
import { InfoBar } from '../InfoBar';
import { BirdBreadcrumbArea } from '../BirdBreadcrumb';
import { StatusLayout } from '../StatusLayout';
export function createChildren(childIds, state, childOptions) {
    if (!state || !childIds) {
        return null;
    }
    const children = [];
    for (const id of childIds) {
        children.push(_jsx(Child, Object.assign({ id: id, state: state }, childOptions), id));
    }
    return children;
}
const BaseChild = ({ id, state, includePreferredSizing, style, contentJustification, actions, }) => {
    const cbRef = useRef(null);
    const onReportContextMenu = (e) => {
        var _a;
        actions.setReportContextMenuTarget(id, e.nativeEvent.timeStamp);
        (_a = cbRef.current) === null || _a === void 0 ? void 0 : _a.call(cbRef, e);
    };
    const centerContent = contentJustification === 'center';
    const useCalculatedHeight = !!includePreferredSizing && hasFlexibleHeight(id, state);
    return (_jsx(ErrorBoundary, Object.assign({ visualId: id, errorMessageKey: { key: 'ReportContainer.component.renderError.txt' } }, { children: useCalculatedHeight ? (_jsx(FlexHeightLayout, Object.assign({ style: style, id: id, onReportContextMenu: onReportContextMenu }, { children: createChildWithStatusAndBreadcrumb(id, state, !!includePreferredSizing, true) }))) : (_jsxs(PositionedLayout, Object.assign({ style: style, id: id, autoLayout: includePreferredSizing, onReportContextMenu: onReportContextMenu, "test-id": exposeTestId() ? id : undefined, contentJustification: contentJustification }, { children: [_jsx(Title, { id: id }), createChildWithStatusAndBreadcrumb(id, state, !!includePreferredSizing, !centerContent)] }))) })));
};
export const Child = connect(undefined, (dispatch) => ({
    actions: bindActionCreators(ReportContextMenuState.actions, dispatch),
}))(BaseChild);
function createChild(id, state, includePreferredSizing) {
    if (state.ButtonBar[id]) {
        return _jsx(BirdButtonBar, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.CheckboxList[id]) {
        return _jsx(BirdCheckboxList, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.RadioList[id]) {
        return _jsx(BirdRadioList, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.ComboBox[id]) {
        return _jsx(BirdComboBox, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.Slider[id]) {
        return _jsx(BirdSlider, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.TextField[id]) {
        return _jsx(BirdTextField, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.Container[id]) {
        return _jsx(Box, { id: id });
    }
    if (state.Crosstab[id]) {
        return _jsx(BirdCrosstab, { id: id });
    }
    if (state.DataVisualizations[id]) {
        return _jsx(BIRDVisualization, { id: id });
    }
    if (state.Image[id]) {
        return _jsx(BirdImage, { id: id });
    }
    if (state.StackContainer[id]) {
        return _jsx(StackContainer, { id: id });
    }
    if (state.PromptContainer[id]) {
        return _jsx(PromptContainer, { id: id });
    }
    if (state.Table[id]) {
        return _jsx(BirdTable, { id: id });
    }
    if (state.Text[id]) {
        return _jsx(BirdText, { id: id });
    }
    if (state.WebContent[id]) {
        return _jsx(BirdWebContent, { id: id });
    }
    if (state.JobContent[id]) {
        return _jsx(BirdJobContent, { id: id });
    }
    if (state.InfoBar[id]) {
        return _jsx(InfoBar, { id: id });
    }
    return _jsx(Unknown, { id: id });
}
function createChildWithStatus(id, state, includePreferredSizing, setWrapperHeight) {
    const isContainer = !!state.Container[id];
    let style = {
        position: 'relative',
        width: '100%',
        height: setWrapperHeight ? '100%' : undefined,
        display: 'flex',
        outline: 'none',
    };
    if (setWrapperHeight) {
        style = Object.assign(Object.assign({}, style), { height: '100%', flexShrink: 1, overflow: 'hidden' });
    }
    return (_jsx("div", Object.assign({ style: style }, { children: _jsx(StatusLayout, Object.assign({ id: id, isContainer: isContainer }, { children: createChild(id, state, includePreferredSizing) })) })));
}
function createChildWithStatusAndBreadcrumb(id, state, includePreferredSizing, setWrapperHeight) {
    if (state.Crosstab[id] || state.DataVisualizations[id]) {
        return (_jsxs(Fragment, { children: [_jsx(BirdBreadcrumbArea, { id: id }), createChildWithStatus(id, state, includePreferredSizing, setWrapperHeight)] }));
    }
    else {
        return createChildWithStatus(id, state, includePreferredSizing, setWrapperHeight);
    }
}
